import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';

// Operation node types with their input/output definitions
interface OperationDefinition {
  id: string;
  name: string;
  category: 'transformation' | 'validation' | 'data_access' | 'control';
  description: string;
  inputs: Array<{
    name: string;
    type: 'any' | 'string' | 'number' | 'boolean' | 'list' | 'dict';
    required: boolean;
  }>;
  outputs: Array<{
    name: string;
    type: 'any' | 'string' | 'number' | 'boolean' | 'list' | 'dict' | 'tuple';
  }>;
  parameters: Array<{
    name: string;
    type: 'string' | 'number' | 'boolean' | 'select';
    defaultValue: any;
    options?: string[];
    description: string;
  }>;
  color: string;
}

// Flow node instance
interface FlowNode {
  id: string;
  operationId: string;
  position: { x: number; y: number };
  parameters: Record<string, any>;
  label: string;
}

// Connection between nodes
interface NodeConnection {
  id: string;
  sourceNodeId: string;
  sourceOutput: string;
  targetNodeId: string;
  targetInput: string;
}

// Available operations from the validation agent
const OPERATION_DEFINITIONS: OperationDefinition[] = [
  // Data Transformation Operations
  {
    id: 'normalize_number',
    name: 'Normalize Number',
    category: 'transformation',
    description: 'Clean and normalize numeric values by removing formatting',
    inputs: [{ name: 'value', type: 'any', required: true }],
    outputs: [{ name: 'normalized', type: 'string' }],
    parameters: [],
    color: '#3B82F6' // Blue
  },
  {
    id: 'extract_percentage',
    name: 'Extract Percentage',
    category: 'transformation',
    description: 'Extract percentage values from text',
    inputs: [{ name: 'text', type: 'string', required: true }],
    outputs: [{ name: 'percentages', type: 'list' }],
    parameters: [],
    color: '#3B82F6'
  },
  {
    id: 'extract_digits',
    name: 'Extract Digits',
    category: 'transformation',
    description: 'Extract only numeric digits from text',
    inputs: [{ name: 'text', type: 'string', required: true }],
    outputs: [{ name: 'digits', type: 'string' }],
    parameters: [],
    color: '#3B82F6'
  },

  // Validation Operations
  {
    id: 'is_empty',
    name: 'Is Empty',
    category: 'validation',
    description: 'Check if value is empty or null',
    inputs: [{ name: 'value', type: 'any', required: true }],
    outputs: [{ name: 'isEmpty', type: 'boolean' }],
    parameters: [],
    color: '#10B981' // Green
  },
  {
    id: 'exact_match',
    name: 'Exact Match',
    category: 'validation',
    description: 'Compare two values for exact equality',
    inputs: [
      { name: 'value1', type: 'any', required: true },
      { name: 'value2', type: 'any', required: true }
    ],
    outputs: [
      { name: 'result', type: 'string' },
      { name: 'score', type: 'number' }
    ],
    parameters: [],
    color: '#10B981'
  },
  {
    id: 'fuzzy_match',
    name: 'Fuzzy Match',
    category: 'validation',
    description: 'Compare two values with similarity threshold',
    inputs: [
      { name: 'value1', type: 'any', required: true },
      { name: 'value2', type: 'any', required: true }
    ],
    outputs: [
      { name: 'result', type: 'string' },
      { name: 'score', type: 'number' }
    ],
    parameters: [
      {
        name: 'threshold',
        type: 'number',
        defaultValue: 95.0,
        description: 'Similarity threshold (0-100)'
      }
    ],
    color: '#10B981'
  },
  {
    id: 'numeric_threshold',
    name: 'Numeric Threshold',
    category: 'validation',
    description: 'Compare numeric values within percentage range',
    inputs: [
      { name: 'value1', type: 'any', required: true },
      { name: 'value2', type: 'any', required: true }
    ],
    outputs: [
      { name: 'result', type: 'string' },
      { name: 'score', type: 'number' }
    ],
    parameters: [
      {
        name: 'threshold_percent',
        type: 'number',
        defaultValue: 10.0,
        description: 'Percentage threshold for comparison'
      }
    ],
    color: '#10B981'
  },

  // Data Access Operations
  {
    id: 'get_table_data',
    name: 'Get Table Data',
    category: 'data_access',
    description: 'Retrieve all records from a database table',
    inputs: [],
    outputs: [{ name: 'records', type: 'list' }],
    parameters: [
      {
        name: 'table_name',
        type: 'select',
        defaultValue: 'vmd',
        options: ['vmd', 'po_header', 'po_line', 'po_history', 'ts007s_history', 'field_config'],
        description: 'Database table name'
      }
    ],
    color: '#F59E0B' // Amber
  },
  {
    id: 'get_record_by_key',
    name: 'Get Record by Key',
    category: 'data_access',
    description: 'Retrieve specific record from database table',
    inputs: [{ name: 'key_value', type: 'any', required: true }],
    outputs: [{ name: 'record', type: 'dict' }],
    parameters: [
      {
        name: 'table',
        type: 'select',
        defaultValue: 'vmd',
        options: ['vmd', 'po_header', 'po_line', 'po_history', 'ts007s_history', 'field_config'],
        description: 'Database table name'
      },
      {
        name: 'key_field',
        type: 'string',
        defaultValue: 'lifnr',
        description: 'Key field name (e.g., lifnr, ebeln)'
      }
    ],
    color: '#F59E0B'
  },
  {
    id: 'get_field_value',
    name: 'Get Field Value',
    category: 'data_access',
    description: 'Extract field value from invoice attributes',
    inputs: [{ name: 'source', type: 'dict', required: true }],
    outputs: [{ name: 'value', type: 'any' }],
    parameters: [
      {
        name: 'field_name',
        type: 'string',
        defaultValue: '',
        description: 'Field name to extract (e.g., "Supplier Name", "PO")'
      }
    ],
    color: '#F59E0B'
  },
  {
    id: 'filter_records',
    name: 'Filter Records',
    category: 'data_access',
    description: 'Filter records based on criteria',
    inputs: [
      { name: 'data', type: 'list', required: true },
      { name: 'criteria', type: 'dict', required: true }
    ],
    outputs: [{ name: 'filtered', type: 'list' }],
    parameters: [],
    color: '#F59E0B'
  }
];

const CATEGORY_COLORS = {
  transformation: '#3B82F6',
  validation: '#10B981',
  data_access: '#F59E0B',
  control: '#8B5CF6'
};

export const ValidationFlowBuilder: React.FC = () => {
  const [nodes, setNodes] = useState<FlowNode[]>([]);
  const [connections, setConnections] = useState<NodeConnection[]>([]);
  const [selectedNode, setSelectedNode] = useState<string | null>(null);
  const [draggedOperation, setDraggedOperation] = useState<string | null>(null);
  const [flowName, setFlowName] = useState('New Validation Flow');
  const [isConnecting, setIsConnecting] = useState<{
    nodeId: string;
    output: string;
  } | null>(null);

  const canvasRef = useRef<HTMLDivElement>(null);
  const [canvasOffset, setCanvasOffset] = useState({ x: 0, y: 0 });

  const handleDragStart = (operationId: string) => {
    setDraggedOperation(operationId);
  };

  const handleDrop = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    if (!draggedOperation || !canvasRef.current) return;

    const rect = canvasRef.current.getBoundingClientRect();
    const x = event.clientX - rect.left - canvasOffset.x;
    const y = event.clientY - rect.top - canvasOffset.y;

    const operation = OPERATION_DEFINITIONS.find(op => op.id === draggedOperation);
    if (!operation) return;

    const newNode: FlowNode = {
      id: `node_${Date.now()}`,
      operationId: draggedOperation,
      position: { x, y },
      parameters: operation.parameters.reduce((acc, param) => {
        acc[param.name] = param.defaultValue;
        return acc;
      }, {} as Record<string, any>),
      label: operation.name
    };

    setNodes(prev => [...prev, newNode]);
    setDraggedOperation(null);
  }, [draggedOperation, canvasOffset]);

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
  };

  const deleteNode = (nodeId: string) => {
    setNodes(prev => prev.filter(node => node.id !== nodeId));
    setConnections(prev => prev.filter(conn => 
      conn.sourceNodeId !== nodeId && conn.targetNodeId !== nodeId
    ));
    if (selectedNode === nodeId) {
      setSelectedNode(null);
    }
  };

  const updateNodeParameter = (nodeId: string, paramName: string, value: any) => {
    setNodes(prev => prev.map(node => 
      node.id === nodeId 
        ? { ...node, parameters: { ...node.parameters, [paramName]: value } }
        : node
    ));
  };

  const startConnection = (nodeId: string, output: string) => {
    setIsConnecting({ nodeId, output });
  };

  const completeConnection = (targetNodeId: string, targetInput: string) => {
    if (!isConnecting) return;

    const newConnection: NodeConnection = {
      id: `conn_${Date.now()}`,
      sourceNodeId: isConnecting.nodeId,
      sourceOutput: isConnecting.output,
      targetNodeId,
      targetInput
    };

    setConnections(prev => [...prev, newConnection]);
    setIsConnecting(null);
  };

  const exportFlow = () => {
    const flow = {
      name: flowName,
      nodes,
      connections,
      created: new Date().toISOString()
    };
    console.log('Exported Flow:', JSON.stringify(flow, null, 2));
    // TODO: Save to backend
  };

  const [showConfigPanel, setShowConfigPanel] = useState(false);
  const [generatedConfig, setGeneratedConfig] = useState('');

  const generateConfiguration = () => {
    setShowConfigPanel(true);
    
    // Simple topological sort for execution order
    const topologicalSort = (nodes: FlowNode[], connections: NodeConnection[]): FlowNode[] => {
      const sorted: FlowNode[] = [];
      const visited = new Set<string>();
      
      const visit = (nodeId: string) => {
        if (visited.has(nodeId)) return;
        visited.add(nodeId);
        
        // Visit dependencies first
        const dependencies = connections
          .filter(conn => conn.targetNodeId === nodeId)
          .map(conn => conn.sourceNodeId);
        
        dependencies.forEach(depId => visit(depId));
        
        const node = nodes.find(n => n.id === nodeId);
        if (node) sorted.push(node);
      };
      
      nodes.forEach(node => visit(node.id));
      return sorted;
    };
    
    // Helper function to get input variable name from connections
    const getInputVariable = (node: FlowNode, inputName: string, connections: NodeConnection[], variables: Map<string, string>): string => {
      const connection = connections.find(conn => 
        conn.targetNodeId === node.id && conn.targetInput === inputName
      );
      
      if (connection && variables.has(connection.sourceNodeId)) {
        return variables.get(connection.sourceNodeId)!;
      }
      
      // Default fallback values
      switch (inputName) {
        case 'key_value':
        case 'value':
        case 'value1':
        case 'value2':
          return '""';
        case 'text':
          return '""';
        case 'data':
          return '[]';
        case 'criteria':
          return '{}';
        default:
          return '""';
      }
    };
    
    // Generate flow execution code that directly calls ValidationOperations static methods
    const generateFlowExecutionCode = () => {
      const codeLines: string[] = [];
      const variables = new Map<string, string>();
      
      // Sort nodes in execution order (topological sort based on connections)
      const sortedNodes = topologicalSort(nodes, connections);
      
      sortedNodes.forEach((node, index) => {
        const operation = OPERATION_DEFINITIONS.find(op => op.id === node.operationId);
        if (!operation) return;
        
        const varName = `result_${index}`;
        variables.set(node.id, varName);
        
        // Generate method call based on operation type
        let methodCall = '';
        
        switch (node.operationId) {
          case 'get_field_value':
            const fieldName = node.parameters.field_name || '';
            methodCall = `ValidationOperations.get_field_value(context.invoice_data, "${fieldName}")`;
            break;
            
          case 'get_record_by_key':
            const table = node.parameters.table || 'vmd';
            const keyField = node.parameters.key_field || 'lifnr';
            const keyValueInput = getInputVariable(node, 'key_value', connections, variables);
            methodCall = `ValidationOperations.get_record_by_key("${table}", "${keyField}", ${keyValueInput})`;
            break;
            
          case 'fuzzy_match':
            const val1Input = getInputVariable(node, 'value1', connections, variables);
            const val2Input = getInputVariable(node, 'value2', connections, variables);
            const threshold = node.parameters.threshold || 95.0;
            methodCall = `ValidationOperations.fuzzy_match(${val1Input}, ${val2Input}, ${threshold})`;
            break;
            
          case 'exact_match':
            const exactVal1 = getInputVariable(node, 'value1', connections, variables);
            const exactVal2 = getInputVariable(node, 'value2', connections, variables);
            methodCall = `ValidationOperations.exact_match(${exactVal1}, ${exactVal2})`;
            break;
            
          case 'numeric_threshold':
            const numVal1 = getInputVariable(node, 'value1', connections, variables);
            const numVal2 = getInputVariable(node, 'value2', connections, variables);
            const thresholdPercent = node.parameters.threshold_percent || 10.0;
            methodCall = `ValidationOperations.numeric_threshold(${numVal1}, ${numVal2}, ${thresholdPercent})`;
            break;
            
          case 'normalize_number':
            const normalizeInput = getInputVariable(node, 'value', connections, variables);
            methodCall = `ValidationOperations.normalize_number(${normalizeInput})`;
            break;
            
          case 'is_empty':
            const emptyInput = getInputVariable(node, 'value', connections, variables);
            methodCall = `ValidationOperations.is_empty(${emptyInput})`;
            break;
            
          case 'extract_digits':
            const digitsInput = getInputVariable(node, 'text', connections, variables);
            methodCall = `ValidationOperations.extract_digits(${digitsInput})`;
            break;
            
          case 'extract_percentage':
            const percentInput = getInputVariable(node, 'text', connections, variables);
            methodCall = `ValidationOperations.extract_percentage(${percentInput})`;
            break;
            
          case 'get_table_data':
            const tableName = node.parameters.table_name || 'vmd';
            methodCall = `ValidationOperations.get_table_data("${tableName}")`;
            break;
            
          case 'filter_records':
            const dataInput = getInputVariable(node, 'data', connections, variables);
            const criteriaInput = getInputVariable(node, 'criteria', connections, variables);
            methodCall = `ValidationOperations.filter_records(${dataInput}, ${criteriaInput})`;
            break;
            
          default:
            methodCall = `# Unknown operation: ${node.operationId}`;
        }
        
        codeLines.push(`        ${varName} = ${methodCall}  # ${node.label}`);
      });
      
      return { code: codeLines.join('\n'), variableCount: variables.size };
    };
    
    const { code: flowExecutionCode, variableCount } = generateFlowExecutionCode();
    const flowNameNormalized = flowName.toLowerCase().replace(/\s+/g, '_');
    
    const integrationInstructions = `# Custom Validation Flow: ${flowName}
# Generated from Field Configuration UI

## Step 1: Add this method to the ValidationRuleEngine class in field_validator_agent.py

def _execute_${flowNameNormalized}_validation(self, rule_config: Dict, context: ValidationContext) -> Dict:
    """Execute ${flowName} validation flow"""
    try:
${flowExecutionCode}
        
        # Return the final result
        return {
            "flow_name": "${flowNameNormalized}",
            "status": "completed",
            "final_result": ${variableCount > 0 ? `result_${variableCount - 1}` : 'None'}
        }
    except Exception as e:
        return {
            "flow_name": "${flowNameNormalized}",
            "status": "error", 
            "error": str(e)
        }

## Step 2: Add the rule configuration to DEFAULT_VALIDATION_RULES

"${flowNameNormalized}": {
    "type": "${flowNameNormalized}_validation",
    "enabled": True,
    "description": "${flowName} validation flow with ${nodes.length} operations"
}

## Step 3: Add case to execute_validation_rule method

elif rule_type == '${flowNameNormalized}_validation':
    return self._execute_${flowNameNormalized}_validation(rule_config, context)

## Step 4: Enable the validation in your config
Add this to your validation configuration to enable the new flow:

app_config.validation_config["enable_${flowNameNormalized}"] = True

## Flow Summary
- **Flow Name**: ${flowName}
- **Operations**: ${nodes.length} nodes configured
- **Connections**: ${connections.length} data flows
- **Operations Used**: ${nodes.map(n => n.operationId).join(', ') || 'None'}

## Example Usage
The flow will be automatically executed when field_validator_agent runs.
Each operation directly calls the existing ValidationOperations static methods.
`;

    setGeneratedConfig(integrationInstructions);
  };


  const renderOperationPalette = () => {
    const categories = ['transformation', 'validation', 'data_access'] as const;
    
    return (
      <div className="w-80 bg-gray-50 dark:bg-gray-800 border-r border-gray-200 dark:border-gray-700 overflow-y-auto">
        <div className="p-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Operations Palette
          </h3>
          
          {categories.map(category => (
            <div key={category} className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2 uppercase tracking-wide">
                {category.replace('_', ' ')}
              </h4>
              <div className="space-y-2">
                {OPERATION_DEFINITIONS
                  .filter(op => op.category === category)
                  .map(operation => (
                    <div
                      key={operation.id}
                      draggable
                      onDragStart={() => handleDragStart(operation.id)}
                      className="p-3 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 cursor-grab hover:shadow-md transition-shadow"
                      style={{ borderLeftColor: operation.color, borderLeftWidth: '4px' }}
                    >
                      <div className="font-medium text-sm text-gray-900 dark:text-gray-100">
                        {operation.name}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                        {operation.description}
                      </div>
                      <div className="flex gap-1 mt-2">
                        <span className="text-xs px-1 bg-blue-100 text-blue-800 rounded">
                          {operation.inputs.length} inputs
                        </span>
                        <span className="text-xs px-1 bg-green-100 text-green-800 rounded">
                          {operation.outputs.length} outputs
                        </span>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderNode = (node: FlowNode) => {
    const operation = OPERATION_DEFINITIONS.find(op => op.id === node.operationId);
    if (!operation) return null;

    const isSelected = selectedNode === node.id;

    return (
      <div
        key={node.id}
        className={`absolute bg-white dark:bg-gray-700 rounded-lg border-2 ${
          isSelected ? 'border-blue-500' : 'border-gray-300 dark:border-gray-600'
        } shadow-lg min-w-48`}
        style={{
          left: node.position.x,
          top: node.position.y,
          borderTopColor: operation.color,
          borderTopWidth: '4px'
        }}
        onClick={() => setSelectedNode(node.id)}
      >
        <div className="p-3">
          <div className="flex items-center justify-between mb-2">
            <h4 className="font-medium text-sm text-gray-900 dark:text-gray-100">
              {node.label}
            </h4>
            <button
              onClick={(e) => {
                e.stopPropagation();
                deleteNode(node.id);
              }}
              className="text-red-500 hover:text-red-700 text-xs"
            >
              ✕
            </button>
          </div>

          {/* Input ports */}
          {operation.inputs.length > 0 && (
            <div className="mb-2">
              <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Inputs:</div>
              {operation.inputs.map(input => (
                <div
                  key={input.name}
                  className="flex items-center mb-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    completeConnection(node.id, input.name);
                  }}
                >
                  <div className="w-3 h-3 bg-blue-500 rounded-full mr-2 cursor-pointer hover:bg-blue-600"></div>
                  <span className="text-xs text-gray-700 dark:text-gray-300">
                    {input.name} ({input.type})
                  </span>
                </div>
              ))}
            </div>
          )}

          {/* Output ports */}
          {operation.outputs.length > 0 && (
            <div className="mb-2">
              <div className="text-xs text-gray-600 dark:text-gray-400 mb-1">Outputs:</div>
              {operation.outputs.map(output => (
                <div
                  key={output.name}
                  className="flex items-center justify-end mb-1"
                  onClick={(e) => {
                    e.stopPropagation();
                    startConnection(node.id, output.name);
                  }}
                >
                  <span className="text-xs text-gray-700 dark:text-gray-300 mr-2">
                    {output.name} ({output.type})
                  </span>
                  <div className="w-3 h-3 bg-green-500 rounded-full cursor-pointer hover:bg-green-600"></div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPropertyPanel = () => {
    if (!selectedNode) {
      return (
        <div className="w-80 bg-gray-50 dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 p-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            Properties
          </h3>
          <p className="text-gray-600 dark:text-gray-400">
            Select a node to configure its properties
          </p>
        </div>
      );
    }

    const node = nodes.find(n => n.id === selectedNode);
    const operation = node ? OPERATION_DEFINITIONS.find(op => op.id === node.operationId) : null;

    if (!node || !operation) return null;

    return (
      <div className="w-80 bg-gray-50 dark:bg-gray-800 border-l border-gray-200 dark:border-gray-700 p-4">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
          Properties
        </h3>
        
        <div className="space-y-4">
          <div>
            <Label htmlFor="node-label">Node Label</Label>
            <Input
              id="node-label"
              value={node.label}
              onChange={(e) => setNodes(prev => prev.map(n => 
                n.id === node.id ? { ...n, label: e.target.value } : n
              ))}
            />
          </div>

          {operation.parameters.map(param => (
            <div key={param.name}>
              <Label htmlFor={`param-${param.name}`}>
                {param.name} - {param.description}
              </Label>
              {param.type === 'select' ? (
                <select
                  id={`param-${param.name}`}
                  value={node.parameters[param.name] || param.defaultValue}
                  onChange={(e) => updateNodeParameter(node.id, param.name, e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                >
                  {param.options?.map(option => (
                    <option key={option} value={option}>{option}</option>
                  ))}
                </select>
              ) : (
                <Input
                  id={`param-${param.name}`}
                  type={param.type === 'number' ? 'number' : 'text'}
                  value={node.parameters[param.name] || param.defaultValue}
                  onChange={(e) => updateNodeParameter(
                    node.id, 
                    param.name, 
                    param.type === 'number' ? parseFloat(e.target.value) : e.target.value
                  )}
                />
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="h-screen flex flex-col bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
        <div className="flex items-center gap-4">
          <Input
            value={flowName}
            onChange={(e) => setFlowName(e.target.value)}
            className="text-lg font-semibold"
          />
        </div>
        <div className="flex gap-2">
          <Button onClick={generateConfiguration} variant="outline">
            Generate Configuration
          </Button>
          <Button onClick={exportFlow}>
            Save Flow
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Operations Palette */}
        {renderOperationPalette()}

        {/* Canvas */}
        <div className="flex-1 relative bg-gray-100 dark:bg-gray-900 overflow-hidden">
          <div
            ref={canvasRef}
            className="w-full h-full relative"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            style={{
              backgroundImage: 'radial-gradient(circle, #e5e7eb 1px, transparent 1px)',
              backgroundSize: '20px 20px'
            }}
          >
            {/* Render connections */}
            <svg className="absolute inset-0 w-full h-full pointer-events-none">
              {connections.map(conn => {
                const sourceNode = nodes.find(n => n.id === conn.sourceNodeId);
                const targetNode = nodes.find(n => n.id === conn.targetNodeId);
                if (!sourceNode || !targetNode) return null;

                const x1 = sourceNode.position.x + 192; // node width
                const y1 = sourceNode.position.y + 60;
                const x2 = targetNode.position.x;
                const y2 = targetNode.position.y + 60;

                return (
                  <line
                    key={conn.id}
                    x1={x1}
                    y1={y1}
                    x2={x2}
                    y2={y2}
                    stroke="#6B7280"
                    strokeWidth="2"
                    markerEnd="url(#arrowhead)"
                  />
                );
              })}
              <defs>
                <marker
                  id="arrowhead"
                  markerWidth="10"
                  markerHeight="7"
                  refX="9"
                  refY="3.5"
                  orient="auto"
                >
                  <polygon points="0 0, 10 3.5, 0 7" fill="#6B7280" />
                </marker>
              </defs>
            </svg>

            {/* Render nodes */}
            {nodes.map(renderNode)}

            {/* Instructions */}
            {nodes.length === 0 && (
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="text-center text-gray-500 dark:text-gray-400">
                  <div className="text-xl mb-2">🎯</div>
                  <p className="text-lg font-medium mb-2">Build Your Validation Flow</p>
                  <p>Drag operations from the left palette onto this canvas</p>
                  <p>Connect them together to create custom validation logic</p>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Properties Panel */}
        {renderPropertyPanel()}
      </div>

      {/* Configuration Panel Modal */}
      {showConfigPanel && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto m-4">
            <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Generated Configuration for field_validator_agent.py
              </h3>
              <button
                onClick={() => setShowConfigPanel(false)}
                className="text-gray-400 hover:text-gray-600 text-2xl font-bold"
              >
                ×
              </button>
            </div>
            
            <div className="p-6 space-y-6">
              {/* Instructions */}
              <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">
                  🚀 Integration Instructions
                </h4>
                <p className="text-blue-800 dark:text-blue-200 text-sm">
                  Copy and paste the code below into your field_validator_agent.py file to integrate this custom validation flow.
                  This will use the real validation operations with actual data.
                </p>
              </div>

              {/* Generated Configuration */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <Label>Configuration Code</Label>
                  <Button
                    size="sm"
                    onClick={() => navigator.clipboard.writeText(generatedConfig)}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    📋 Copy to Clipboard
                  </Button>
                </div>
                <pre className="w-full h-96 px-4 py-3 border border-gray-300 rounded-md bg-gray-50 dark:bg-gray-700 text-xs overflow-auto font-mono">
                  {generatedConfig}
                </pre>
              </div>

              {/* Flow Summary */}
              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                <h4 className="font-medium text-green-900 dark:text-green-100 mb-2">✅ Flow Summary</h4>
                <div className="text-green-800 dark:text-green-200 text-sm space-y-1">
                  <p>• **Flow Name**: {flowName}</p>
                  <p>• **Operations**: {nodes.length} nodes configured</p>
                  <p>• **Connections**: {connections.length} data flows between operations</p>
                  <p>• **Operations Used**: {nodes.map(n => n.operationId).join(', ') || 'None'}</p>
                  <p>• **Ready for Integration**: ✅ Copy the code above and follow the steps</p>
                </div>
              </div>

              {/* Next Steps */}
              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-lg">
                <h4 className="font-medium text-yellow-900 dark:text-yellow-100 mb-2">📝 Next Steps</h4>
                <div className="text-yellow-800 dark:text-yellow-200 text-sm space-y-2">
                  <p>1. **Copy the configuration** using the button above</p>
                  <p>2. **Open field_validator_agent.py** in your code editor</p>
                  <p>3. **Follow the step-by-step instructions** in the configuration</p>
                  <p>4. **Test your validation** by running the field validator agent</p>
                  <p>5. **Your custom flow will execute** with real invoice data and validation operations</p>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                <Button 
                  onClick={() => navigator.clipboard.writeText(generatedConfig)}
                  className="bg-green-600 hover:bg-green-700"
                >
                  📋 Copy Configuration
                </Button>
                <Button variant="outline" onClick={() => setShowConfigPanel(false)}>
                  Close
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};